/**
 * TMS转运任务需要的状态和操作集中管理
 */
import { create } from 'zustand';
import { useEffect } from 'react';
import { immer } from 'zustand/middleware/immer';
import { GetTmsPublishedTaskResponse, GET_TMS_PUBLISHED_TASK_BY_DRIVER, TmsWaypointWithAddressType } from '../graphql/queries/getTmsPublishedTaskByDriverAccountId';
import { client } from '../../../graphql/client';
import { ApiResult } from '../../../graphql/types/ApiResult';
import { gql } from '@apollo/client';
import { CREATE_TMS_EXCEPTION, CreateTmsExceptionInput, CreateTmsExceptionResponse } from '../graphql/mutations/createTmsException';
import { GET_TMS_TASK_BY_ID, GetTmsTaskByIdResponse } from '../graphql/queries/getTmsTaskById';
import { GET_TMS_EXCEPTIONS_BY_CURRENT_ACCOUNT, GetTmsExceptionsByCurrentAccountResponse, TmsExceptionType } from '../graphql/queries/getTmsExceptionsByCurrentAccount';
import { GET_TMS_PUBLISHED_TASK_BY_DRIVER_BY_DATE, GetTmsPublishedTaskByDriverByDateResponse, TmsCompletedTaskWithWayPoints, TmsTaskWayPointWithAddressType } from '../graphql/queries/getTmsPublishedTaskByDriverByDate';
import dayjs from 'dayjs';

const UPDATE_TMS_TASK_WAYPOINT_TO_ARRIVED = gql`
    mutation UpdateTmsTaskWaypointToArrived($input: UpdateTmsTaskWaypointToArrivedInput!) {
        updateTmsTaskWaypointToArrived(input: $input)
    }
`;

const UPDATE_TMS_TASK_WAYPOINT_TO_DEPARTURE = gql`
    mutation UpdateTmsTaskWaypointToDeparture($input: UpdateTmsTaskWaypointToDepartureInput!) {
        updateTmsTaskWaypointToDeparture(input: $input) {
            success
            code
            message
        }
    }
`;

export interface UpdateTmsTaskWaypointToArrivedInput {
    tmsTaskWayPointId: number;
    taskId: number;
    photosS3KeysJson: string[];
    arrivalSealPhotosJson?: string[];
    arrivedRemark?: string;
    operationSource?: number;
}

export interface UpdateTmsTaskWaypointToArrivedVariables {
    input: UpdateTmsTaskWaypointToArrivedInput;
}

export interface UpdateTmsTaskWaypointToArrivedResponse {
    updateTmsTaskWaypointToArrived: boolean;
}

export interface TmsTaskWaypointByIdResult {
    id: string;
    taskId: number;
    waypointId: number;
    routeId: number;
    sealNumber?: string | null;
    sealTime?: string | null;
    checkSealNumberFlag: boolean;
}

// 轮询间隔：3分钟
const POLLING_INTERVAL = 3 * 60 * 1000;

// 辅助函数：深度比较两个数组是否相等（不依赖顺序）
const arraysEqual = (a: TmsWaypointWithAddressType[], b: TmsWaypointWithAddressType[]): boolean => {
    if (a.length !== b.length) return false;

    // 按 taskWayPointId 数值排序后比较，避免顺序影响
    const idNum = (id: string | number | null | undefined) => {
        const n = typeof id === 'number' ? id : Number(id);
        return Number.isFinite(n) ? n : -1;
    };
    const sortedA = [...a].sort((x, y) => idNum(x.taskWayPointId) - idNum(y.taskWayPointId));
    const sortedB = [...b].sort((x, y) => idNum(x.taskWayPointId) - idNum(y.taskWayPointId));

    return sortedA.every((itemA, index) => {
        const itemB = sortedB[index];
        if (!itemB) return false;

        // 比较关键字段，忽略可能变化的时间戳等
        return itemA.taskWayPointId === itemB.taskWayPointId &&
               itemA.status === itemB.status &&
               itemA.sequence === itemB.sequence &&
               itemA.isFinished === itemB.isFinished;
    });
};

interface TransportStoreImpl {
    tmsTaskWaypointList: TmsWaypointWithAddressType[];
    loading: boolean;
    _isNeedRefresh: boolean;
    _pollingTimer: NodeJS.Timeout | null;
    _pollingEnabled: boolean;
    taskNumber: string;
    loadingTask: boolean;
    createTransportException: (params: {
        taskId: number;
        exceptionReasonKey: string; // e.g. 'VEHICLE_BREAKDOWN'
        location: string;
        remark?: string | null;
        photoKeys: string[];
        exceptionWarehouseId?: number;
    }) => Promise<boolean>;
    setTmsTaskWaypointList: (tmsTaskWaypointList: TmsWaypointWithAddressType[]) => void;
    fetchTmsTaskWaypointList: () => Promise<TmsWaypointWithAddressType[]>;
    _fetchTmsTaskWaypointList: () => Promise<TmsWaypointWithAddressType[]>;
    refreshTmsTaskWaypointList: () => Promise<TmsWaypointWithAddressType[]>;
    isNeedRefresh: () => boolean;
    setNeedRefresh: (isNeedRefresh: boolean) => void;
    startPolling: () => void;
    stopPolling: () => void;
    updateTmsTaskWaypointToArrived: (input: UpdateTmsTaskWaypointToArrivedInput, optimisticSubmission: boolean) => Promise<boolean>;
    _updateTmsTaskWaypoint: (waypoint: Partial<TmsWaypointWithAddressType>) => void;
    fetchTmsTaskWaypointById: (id: string | number) => Promise<TmsTaskWaypointByIdResult | null>;
        updateTmsTaskWaypointToDeparture: (input: { tmsTaskWayPointId: number; taskId: number; departurePhotosJson?: string[]; departureSealPhotosJson?: string[]; departureRemark?: string; operationSource?: number; }) => Promise<ApiResult>;
    fetchTmsTaskById: (taskId: string) => Promise<void>;
    fetchTmsExceptionsByCurrentAccount: () => Promise<TmsExceptionType[]>;
    tmsExceptions: TmsExceptionType[];
    loadingExceptions: boolean;
    // 已完成任务相关
    completedTmsTaskList: TmsCompletedTaskWithWayPoints[];
    completedTmsTaskLoading: boolean;
    _completedTmsTaskIsNeedRefresh: boolean;
    setCompletedTmsTaskList: (completedTmsTaskList: TmsCompletedTaskWithWayPoints[]) => void;
    fetchCompletedTmsTaskList: (selectedDate?: string) => Promise<TmsCompletedTaskWithWayPoints[]>;
    _fetchCompletedTmsTaskList: (selectedDate?: string) => Promise<TmsCompletedTaskWithWayPoints[]>;
    refreshCompletedTmsTaskList: (selectedDate?: string) => Promise<TmsCompletedTaskWithWayPoints[]>;
    isCompletedTmsTaskNeedRefresh: () => boolean;
    setCompletedTmsTaskNeedRefresh: (isNeedRefresh: boolean) => void;
    fetchCompletedTmsTaskWaypointDetail: (taskId: string | number, taskWayPointId: string | number, selectedDate: string) => Promise<TmsTaskWayPointWithAddressType | null>;
    getWaypointLoadingParcelCountsById: (waypointId: number) => Promise<number>;
    getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId: (taskId: number, waypointId: number) => Promise<number>;
}

const useTransportStore = create<TransportStoreImpl>()(
    immer((set, get) => ({
        tmsTaskWaypointList: [],
        loading: false,
        _isNeedRefresh: false,
        _pollingTimer: null,
        _pollingEnabled: false,
        taskNumber: '',
        loadingTask: false,
        tmsExceptions: [],
        loadingExceptions: false,
        // 已完成任务相关状态
        completedTmsTaskList: [],
        completedTmsTaskLoading: false,
        _completedTmsTaskIsNeedRefresh: false,
        setNeedRefresh: (isNeedRefresh: boolean) => {
            set({ _isNeedRefresh: isNeedRefresh });
        },
        // 创建异常记录
        createTransportException: async ({ taskId, exceptionReasonKey, location, remark, photoKeys, exceptionWarehouseId }) => {
            // 将异常原因枚举Key映射为服务端的Int代码
            const exceptionReasonMap: Record<string, number> = {
                VEHICLE_BREAKDOWN: 1,
                TRAFFIC_ACCIDENT: 2,
                WEATHER: 3,
                TRAFFIC_JAM: 4,
                WAREHOUSE_DELAY: 5,
                OTHER: 6
            };
            const exceptionReasonType = exceptionReasonMap[exceptionReasonKey] ?? 9;
            const input: CreateTmsExceptionInput = {
                taskId,
                exceptionReasonType,
                location,
                remark: remark ?? null,
                exceptionPhotosJson: photoKeys,
                exceptionWarehouseId: exceptionWarehouseId ?? null
            };
            try {
                const { data } = await client.mutate<CreateTmsExceptionResponse, { input: CreateTmsExceptionInput }>({
                    mutation: CREATE_TMS_EXCEPTION,
                    variables: { input }
                });
                return !!data?.createTmsException?.id;
            } catch (error) {
                console.error('createTransportException error:', error);
                return false;
            }
        },
        setTmsTaskWaypointList: (tmsTaskWaypointList) => {
            set({ tmsTaskWaypointList });
        },
        fetchTmsTaskWaypointList: async () => {
            const state = get();
            if (!state.isNeedRefresh() && !state.loading) {
                return state.tmsTaskWaypointList;
            }
            // 防止并发请求
            if (state.loading) {
                return state.tmsTaskWaypointList;
            }
            return get()._fetchTmsTaskWaypointList();
        },
        _fetchTmsTaskWaypointList: async () => {
            set({ loading: true });
            try {
                const { data } = await client.query<GetTmsPublishedTaskResponse>({
                    query: GET_TMS_PUBLISHED_TASK_BY_DRIVER
                });
                const newTmsTaskWaypointList = data?.getTmsPublishedTaskByDriver || [];
                const currentList = get().tmsTaskWaypointList;

                // 只有当数据真正发生变化时才更新状态
                const hasChanges = !arraysEqual(currentList, newTmsTaskWaypointList);
                if (hasChanges) {
                    set({ tmsTaskWaypointList: newTmsTaskWaypointList });
                }

                return get().tmsTaskWaypointList;
            } catch (error) {
                console.error('Error fetching TMS task waypoint list:', error);
                return get().tmsTaskWaypointList;
            } finally {
                set({ loading: false });
                get().setNeedRefresh(false);
            }
        },
        refreshTmsTaskWaypointList: async () => {
            get().setNeedRefresh(true);
            return get()._fetchTmsTaskWaypointList();
        },
        // 判断是否需要重新获取列表
        isNeedRefresh: () => {
            return get().tmsTaskWaypointList.length === 0 || get()._isNeedRefresh;
        },
        // 开始轮询
        startPolling: () => {
            const state = get();
            if (state._pollingTimer || state._pollingEnabled) {
                return; // 已经在轮询中
            }

            set({ _pollingEnabled: true });

            const poll = async () => {
                if (!get()._pollingEnabled) {
                    return;
                }

                try {
                    console.debug('Polling TMS task waypoint list...');
                    await get().refreshTmsTaskWaypointList();
                } catch (error) {
                    console.error('Polling error:', error);
                }

                // 设置下一次轮询
                if (get()._pollingEnabled) {
                    const timer = setTimeout(poll, POLLING_INTERVAL);
                    set({ _pollingTimer: timer });
                }
            };

            // 立即开始第一次轮询
            const timer = setTimeout(poll, POLLING_INTERVAL);
            set({ _pollingTimer: timer });
        },
        // 停止轮询
        stopPolling: () => {
            const state = get();
            if (state._pollingTimer) {
                clearTimeout(state._pollingTimer);
            }
            set({
                _pollingTimer: null,
                _pollingEnabled: false
            });
        },
        // 更新途经点状态（内部方法）
        _updateTmsTaskWaypoint: (waypoint: Partial<TmsWaypointWithAddressType>) => {
            const waypointId = waypoint.taskWayPointId;
            if (!waypointId) {
                return;
            }
            set((state) => {
                const index = state.tmsTaskWaypointList.findIndex(
                    (item) => item.taskWayPointId === String(waypointId)
                );
                if (index !== -1) {
                    state.tmsTaskWaypointList[index] = {
                        ...state.tmsTaskWaypointList[index],
                        ...waypoint,
                    };
                }
            });
        },
        // 更新途经点状态为到达
        updateTmsTaskWaypointToArrived: async (input: UpdateTmsTaskWaypointToArrivedInput, optimisticSubmission: boolean) => {
            const setTmsTaskWaypointToArrived = () => {
                const waypointState: Partial<TmsWaypointWithAddressType> = {
                    taskWayPointId: String(input.tmsTaskWayPointId),
                    status: 'ARRIVED',
                    isFinished: false,
                };
                get()._updateTmsTaskWaypoint(waypointState);
            };
            if (optimisticSubmission === true) {
                //仅执行乐观提交逻辑，不进行网络请求
                setTmsTaskWaypointToArrived();
                return true;
            } else {
                try {
                    console.debug('updateTmsTaskWaypointToArrived begin:', input);
                    const result = await client.mutate<UpdateTmsTaskWaypointToArrivedResponse, UpdateTmsTaskWaypointToArrivedVariables>({
                        mutation: UPDATE_TMS_TASK_WAYPOINT_TO_ARRIVED,
                        variables: { input }
                    });
                    console.debug('updateTmsTaskWaypointToArrived result:', result);
                    const success = result.data?.updateTmsTaskWaypointToArrived || false;
                    if (success) {
                        setTmsTaskWaypointToArrived();
                    }
                    return success;
                } catch (error: any) {
                    console.error('updateTmsTaskWaypointToArrived catch error:', error);
                    if (error.networkError) {
                        // 乐观提交：网络错误，更新途经点状态
                        console.debug('updateTmsTaskWaypointToArrived networkError', error);
                        setTmsTaskWaypointToArrived();
                    }
                    throw error;
                }
            }
        },
        // 更新途经点为发车（Departure）
        updateTmsTaskWaypointToDeparture: async (input: { tmsTaskWayPointId: number; taskId: number; departurePhotosJson?: string[]; departureSealPhotosJson?: string[]; departureRemark?: string; operationSource?: number; }): Promise<ApiResult> => {
            try {
                const result = await client.mutate<{ updateTmsTaskWaypointToDeparture: ApiResult }, { input: { tmsTaskWayPointId: number; taskId: number; departurePhotosJson?: string[]; departureSealPhotosJson?: string[]; departureRemark?: string; operationSource?: number; } }>({
                    mutation: UPDATE_TMS_TASK_WAYPOINT_TO_DEPARTURE,
                    variables: { input }
                });
                const apiResult = result.data?.updateTmsTaskWaypointToDeparture ?? ApiResult.error();
                if (apiResult.success) {
                    // 本地更新状态
                    get()._updateTmsTaskWaypoint({
                        taskWayPointId: String(input.tmsTaskWayPointId),
                        status: 'DONE',
                        isFinished: false,
                    });
                }
                return apiResult;
            } catch (error) {
                console.error('updateTmsTaskWaypointToDeparture error:', error);
                return ApiResult.error(error instanceof Error ? error.message : String(error));
            }
        },
        // 根据ID查询TMS任务途经点（精简字段）
        fetchTmsTaskWaypointById: async (id: string | number) => {
            const GET_TMS_TASK_WAYPOINT_BY_ID = gql`
                query GetTmsTaskWaypointById($id: ID!) {
                  getTmsTaskWaypointById(id: $id) {
                    id
                    taskId
                    waypointId
                    routeId
                    sealNumber
                    sealTime
                    checkSealNumberFlag
                  }
                }
            `;
            try {
                const { data } = await client.query<{ getTmsTaskWaypointById: { id: string; taskId: number; waypointId: number; routeId: number; sealNumber?: string | null; sealTime?: string | null; checkSealNumberFlag: boolean } | null }, { id: string | number }>({
                    query: GET_TMS_TASK_WAYPOINT_BY_ID,
                    variables: { id },
                    fetchPolicy: 'no-cache'
                });
                return data?.getTmsTaskWaypointById ?? null;
            } catch (error) {
                console.error('fetchTmsTaskWaypointById error:', error);
                return null;
            }
        },
        // 根据ID查询TMS任务信息
        fetchTmsTaskById: async (taskId: string) => {
            if (!taskId) {
                return;
            }

            set({ loadingTask: true });
            try {
                const { data } = await client.query<GetTmsTaskByIdResponse>({
                    query: GET_TMS_TASK_BY_ID,
                    variables: { id: taskId },
                    fetchPolicy: 'no-cache'
                });

                if (data?.getTmsTaskById?.taskNumber) {
                    set({ taskNumber: data.getTmsTaskById.taskNumber });
                }
            } catch (error) {
                console.error('Failed to fetch task info:', error);
            } finally {
                set({ loadingTask: false });
            }
        },
        // 查询当前账号的异常记录列表
        fetchTmsExceptionsByCurrentAccount: async () => {
            set({ loadingExceptions: true });
            try {
                const { data } = await client.query<GetTmsExceptionsByCurrentAccountResponse>({
                    query: GET_TMS_EXCEPTIONS_BY_CURRENT_ACCOUNT,
                    fetchPolicy: 'no-cache'
                });
                const exceptions = data?.getTmsExceptionsByCurrentAccount || [];
                set({ tmsExceptions: exceptions });
                return exceptions;
            } catch (error) {
                console.error('Failed to fetch TMS exceptions:', error);
                set({ tmsExceptions: [] });
                return [];
            } finally {
                set({ loadingExceptions: false });
            }
        },
        // 已完成任务相关方法
        setCompletedTmsTaskList: (completedTmsTaskList) => {
            set({ completedTmsTaskList });
        },
        setCompletedTmsTaskNeedRefresh: (isNeedRefresh: boolean) => {
            set({ _completedTmsTaskIsNeedRefresh: isNeedRefresh });
        },
        fetchCompletedTmsTaskList: async (selectedDate?: string) => {
            if (!get().isCompletedTmsTaskNeedRefresh()) {
                return get().completedTmsTaskList;
            }
            return get()._fetchCompletedTmsTaskList(selectedDate);
        },
        _fetchCompletedTmsTaskList: async (selectedDate?: string) => {
            set({ completedTmsTaskLoading: true });
            try {
                const dateToUse = selectedDate || dayjs().format('YYYY-MM-DD').toString();
                const { data } = await client.query<GetTmsPublishedTaskByDriverByDateResponse>({
                    query: GET_TMS_PUBLISHED_TASK_BY_DRIVER_BY_DATE,
                    variables: { taskDate: dateToUse },
                    fetchPolicy: 'no-cache'
                });
                const completedTmsTaskList = data?.getTmsPublishedTaskByDriverByDate || [];
                set({ completedTmsTaskList });
                return get().completedTmsTaskList;
            } catch (error) {
                console.error('Error fetching completed TMS task list:', error);
                return get().completedTmsTaskList;
            } finally {
                set({ completedTmsTaskLoading: false });
                get().setCompletedTmsTaskNeedRefresh(false);
            }
        },
        refreshCompletedTmsTaskList: async (selectedDate?: string) => {
            get().setCompletedTmsTaskNeedRefresh(true);
            return get()._fetchCompletedTmsTaskList(selectedDate);
        },
        isCompletedTmsTaskNeedRefresh: () => {
            return get().completedTmsTaskList.length === 0 || get()._completedTmsTaskIsNeedRefresh;
        },
        // 获取已完成任务的途径点详情
        fetchCompletedTmsTaskWaypointDetail: async (taskId: string | number, taskWayPointId: string | number, selectedDate: string) => {
            try {
                // 先获取任务列表
                await get()._fetchCompletedTmsTaskList(selectedDate);
                const tasks = get().completedTmsTaskList;
                
                const numericTaskId = Number(taskId);
                const numericTaskWayPointId = Number(taskWayPointId);
                
                // 找到对应的任务
                const task = tasks.find(
                    t => Number(t.taskId) === numericTaskId || String(t.taskId) === String(numericTaskId)
                );
                
                if (!task) {
                    console.warn('Task not found:', {
                        lookingFor: numericTaskId,
                        available: tasks.map(t => t.taskId)
                    });
                    return null;
                }
                
                // 找到对应的途径点
                const waypoint = task.wayPoints.find(
                    wp => Number(wp.taskWayPointId) === numericTaskWayPointId || String(wp.taskWayPointId) === String(numericTaskWayPointId)
                );
                
                if (!waypoint) {
                    console.warn('Waypoint not found:', {
                        lookingFor: numericTaskWayPointId,
                        available: task.wayPoints.map(wp => wp.taskWayPointId)
                    });
                    return null;
                }
                
                return waypoint;
            } catch (error) {
                console.error('Failed to fetch completed TMS task waypoint detail:', error);
                return null;
            }
        },
        // 根据途经点ID查询装车包裹数量
        getWaypointLoadingParcelCountsById: async (waypointId: number) => {
            const GET_WAYPOINT_LOADING_PARCEL_COUNTS_BY_ID = gql`
                query GetWaypointLoadingParcelCountsById($waypointId: Long!) {
                    getWaypointLoadingParcelCountsById(waypointId: $waypointId)
                }
            `;
            try {
                const { data } = await client.query<{ getWaypointLoadingParcelCountsById: number }, { waypointId: number }>({
                    query: GET_WAYPOINT_LOADING_PARCEL_COUNTS_BY_ID,
                    variables: { waypointId },
                    fetchPolicy: 'no-cache'
                });
                return data?.getWaypointLoadingParcelCountsById ?? 0;
            } catch (error) {
                console.error('getWaypointLoadingParcelCountsById error:', error);
                return 0;
            }
        },
        // 根据任务ID和途经点ID查询装车包裹数量
        getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId: async (taskId: number, waypointId: number) => {
            const GET_LOADING_ORDER_DETAIL_WAYPOINT_COUNT_BY_TASK_ID_AND_WAYPOINT_ID = gql`
                query GetLoadingOrderDetailWaypointCountByTaskIdAndWaypointId($taskId: Long!, $waypointId: Long!) {
                    getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId(taskId: $taskId, waypointId: $waypointId) {
                        waypointId
                        parcelCount
                        bagCount
                    }
                }
            `;
            try {
                const { data } = await client.query<{ getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId: { waypointId: number; parcelCount: number; bagCount: number } | null }, { taskId: number; waypointId: number }>({
                    query: GET_LOADING_ORDER_DETAIL_WAYPOINT_COUNT_BY_TASK_ID_AND_WAYPOINT_ID,
                    variables: { taskId, waypointId },
                    fetchPolicy: 'no-cache'
                });
                return data?.getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId?.parcelCount ?? 0;
            } catch (error) {
                console.error('getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId error:', error);
                return 0;
            }
        }
    }))
);

export const useTmsTaskWaypointList = () => {
    const { tmsTaskWaypointList, fetchTmsTaskWaypointList, refreshTmsTaskWaypointList, loading, startPolling, stopPolling } = useTransportStore(state => ({
        tmsTaskWaypointList: state.tmsTaskWaypointList,
        fetchTmsTaskWaypointList: state.fetchTmsTaskWaypointList,
        refreshTmsTaskWaypointList: state.refreshTmsTaskWaypointList,
        loading: state.loading,
        startPolling: state.startPolling,
        stopPolling: state.stopPolling,
    }));

    useEffect(() => {
        // 初始化时获取数据并开始轮询
        fetchTmsTaskWaypointList();
        startPolling();

        // 组件卸载时停止轮询
        return () => {
            stopPolling();
        };
    }, []);

    return { tmsTaskWaypointList, loading, refreshTmsTaskWaypointList };
};

export const useTmsTaskWaypoint = (taskWayPointId: string | number | null | undefined) => {
    const { tmsTaskWaypointList, refreshTmsTaskWaypointList, loading } = useTransportStore(state => ({
        tmsTaskWaypointList: state.tmsTaskWaypointList,
        refreshTmsTaskWaypointList: state.refreshTmsTaskWaypointList,
        loading: state.loading
    }));
    const normalizedIdStr =
        taskWayPointId === null || taskWayPointId === undefined ? undefined : String(taskWayPointId);
    return {
        tmsTaskWaypoint: tmsTaskWaypointList.find(item => item.taskWayPointId === normalizedIdStr),
        loading,
        refreshTmsTaskWaypointList
    };
};

export const useTmsTaskOperations = () => {
    const { updateTmsTaskWaypointToArrived, fetchTmsTaskWaypointById, updateTmsTaskWaypointToDeparture } = useTransportStore(state => ({
        updateTmsTaskWaypointToArrived: state.updateTmsTaskWaypointToArrived,
        fetchTmsTaskWaypointById: state.fetchTmsTaskWaypointById,
        updateTmsTaskWaypointToDeparture: state.updateTmsTaskWaypointToDeparture
    }));
    return { updateTmsTaskWaypointToArrived, fetchTmsTaskWaypointById, updateTmsTaskWaypointToDeparture };
};

// 导出store实例，用于在非React组件中访问
export { useTransportStore };

// 已完成任务列表 hook
export const useCompletedTmsTaskList = (selectedDate?: string) => {
    const {
        completedTmsTaskList,
        fetchCompletedTmsTaskList,
        refreshCompletedTmsTaskList,
        completedTmsTaskLoading,
    } = useTransportStore(state => ({
        completedTmsTaskList: state.completedTmsTaskList,
        fetchCompletedTmsTaskList: state.fetchCompletedTmsTaskList,
        refreshCompletedTmsTaskList: state.refreshCompletedTmsTaskList,
        completedTmsTaskLoading: state.completedTmsTaskLoading,
    }));

    useEffect(() => {
        // 初始化时获取数据，使用传入的selectedDate或当前日期
        const dateToUse = selectedDate || dayjs().format('YYYY-MM-DD').toString();
        fetchCompletedTmsTaskList(dateToUse);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    return {
        completedTmsTaskList,
        loading: completedTmsTaskLoading,
        refreshCompletedTmsTaskList,
    };
};

